/**
 * CloneBlockPopover — C7-2 options popover for "Clone Block…".
 *
 * Approved design: .design/block-clone-variants/mockups/node-clone-menu.html
 * (state 3 — single clone). The "Incoming connections" setting is the SI-1/SI-2
 * user pin resolved AFTER the mock gate: default `disconnected` (clone gets no
 * traffic until wired — safe for comparing variants), option `fork` (the
 * predecessors' edges also point at the clone). Outgoing edges are governed by
 * the "Keep outgoing connections" checkbox (server preserveEdges).
 *
 * Variant fan-out controls land with the variant executor (C7-4).
 */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AvailableModelSelector, useProjectDefaultAIModel } from '@modules/ai-settings';
import type { CloneIncomingMode } from '@modules/reactFlow/commands/CloneBlockAdvancedCommand';

export interface CloneBlockPopoverSubmit {
  copyScreens: boolean;
  keepOutgoing: boolean;
  incomingMode: CloneIncomingMode;
}

/** C7-4: "Clone & Generate N Variants" — variants are ALWAYS disconnected-in
 * (approved canvas mock: dashed stubs until wired/Replaced). */
export interface CloneBlockVariantsSubmit {
  variantCount: number;
  angleHints: string[];
  copyScreens: boolean;
  keepOutgoing: boolean;
  /**
   * Variant-generation model, chosen HERE (operator decision 2026-08-05 §2:
   * AI actions expose the model at the launch surface — only the user's
   * connected models are offered, never a platform default).
   */
  model?: string;
}

export interface CloneBlockPopoverProps {
  open: boolean;
  anchorPosition: { top: number; left: number } | null;
  blockName: string;
  projectId: string;
  busy?: boolean;
  onClose: () => void;
  onClone: (options: CloneBlockPopoverSubmit) => void;
  onCloneVariants: (options: CloneBlockVariantsSubmit) => void;
}

export const CloneBlockPopover: React.FC<CloneBlockPopoverProps> = ({
  open,
  anchorPosition,
  blockName,
  projectId,
  busy = false,
  onClose,
  onClone,
  onCloneVariants,
}) => {
  const [copyScreens, setCopyScreens] = useState(true);
  const [keepOutgoing, setKeepOutgoing] = useState(true);
  const [incomingMode, setIncomingMode] = useState<CloneIncomingMode>('disconnected');
  const [variantCount, setVariantCount] = useState(1);
  const [angleHints, setAngleHints] = useState<string[]>(['', '', '']);
  // «Clone & Generate» spawns AI work → the model is chosen here (user's
  // connected models only, prefilled with the project default).
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [model, setModel] = useState(projectDefaultModel.value);

  useEffect(() => {
    if (open) {
      setCopyScreens(true);
      setKeepOutgoing(true);
      setIncomingMode('disconnected');
      setVariantCount(1);
      setAngleHints(['', '', '']);
      setModel(projectDefaultModel.value);
    }
  }, [open, projectDefaultModel.value]);

  return (
    <Popover
      open={open && Boolean(anchorPosition)}
      onClose={busy ? undefined : onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? { top: 0, left: 0 }}
      slotProps={{ paper: { sx: { width: 360, p: 2.5 }, 'data-testid': 'clone-block-popover' } as never }}
    >
      <Stack spacing={1.5}>
        <div>
          <Typography variant="subtitle1" fontWeight={600}>Clone {blockName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Creates a sibling copy next to the source block
          </Typography>
        </div>

        <FormControlLabel
          control={(
            <Checkbox
              checked={copyScreens}
              onChange={(event) => setCopyScreens(event.target.checked)}
              data-testid="clone-block-copy-screens"
            />
          )}
          label={(
            <div>
              <Typography variant="body2">Copy screens</Typography>
              <Typography variant="caption" color="text.secondary">
                Include the source&apos;s existing screens; generation updates them
              </Typography>
            </div>
          )}
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={keepOutgoing}
              onChange={(event) => setKeepOutgoing(event.target.checked)}
              data-testid="clone-block-keep-outgoing"
            />
          )}
          label={(
            <div>
              <Typography variant="body2">Keep outgoing connections</Typography>
              <Typography variant="caption" color="text.secondary">
                The clone points to the same next blocks as the source
              </Typography>
            </div>
          )}
        />

        <div>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Incoming connections</Typography>
          <RadioGroup
            value={incomingMode}
            onChange={(event) => setIncomingMode(event.target.value as CloneIncomingMode)}
          >
            <FormControlLabel
              value="disconnected"
              control={<Radio size="small" data-testid="clone-block-incoming-disconnected" />}
              label={(
                <div>
                  <Typography variant="body2">Start disconnected</Typography>
                  <Typography variant="caption" color="text.secondary">
                    No traffic reaches the clone until you wire an edge — safe for comparing variants
                  </Typography>
                </div>
              )}
            />
            <FormControlLabel
              value="fork"
              control={<Radio size="small" data-testid="clone-block-incoming-fork" />}
              label={(
                <div>
                  <Typography variant="body2">Fork incoming edges</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Every edge that points at the source also points at the clone
                  </Typography>
                </div>
              )}
            />
          </RadioGroup>
        </div>

        <div>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Variants</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={variantCount}
            onChange={(_event, value) => { if (value !== null) setVariantCount(value); }}
            aria-label="variant count"
            data-testid="clone-block-variant-count"
          >
            {[1, 2, 3].map((count) => (
              <ToggleButton key={count} value={count} data-testid={`clone-block-variant-count-${count}`}>
                {count}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        {variantCount > 1 && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Variant angles — optional, empty = auto from unused evidence
            </Typography>
            {Array.from({ length: variantCount }, (_item, index) => (
              <TextField
                key={index}
                size="small"
                label={`Variant ${index + 1} angle`}
                value={angleHints[index] ?? ''}
                onChange={(event) => {
                  setAngleHints((prev) => {
                    const next = [...prev];
                    next[index] = event.target.value;
                    return next;
                  });
                }}
                inputProps={{ 'data-testid': `clone-block-variant-angle-${index + 1}` } as never}
              />
            ))}
          </Stack>
        )}

        <AvailableModelSelector
          projectId={projectId}
          value={model}
          onChange={setModel}
          label="AI Model"
          size="small"
          fullWidth
          disabled={busy || projectDefaultModel.loading}
          testId="clone-block-model"
        />

        <Typography variant="caption" color="text.secondary">
          {variantCount > 1
            ? 'Runs through the task queue, one variant at a time. Variants start disconnected-in; each uses this block’s AI Data Usage — open Generate Screens on a clone to override its data.'
            : 'AI generation on a disconnected clone still sees the source block’s neighbors (clone provenance) — open Generate Screens on the clone to override its data.'}
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onClone({ copyScreens, keepOutgoing, incomingMode })}
            disabled={busy}
            data-testid="clone-block-submit"
          >
            Clone only
          </Button>
          <Button
            variant="contained"
            onClick={() => onCloneVariants({
              variantCount,
              angleHints: angleHints.slice(0, variantCount),
              copyScreens,
              keepOutgoing,
              ...(model ? { model } : {}),
            })}
            disabled={busy || projectDefaultModel.loading || !model}
            data-testid="clone-block-generate-variants"
          >
            {busy ? 'Working…' : `Clone & Generate ${variantCount} Variant${variantCount > 1 ? 's' : ''}`}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
